import {
  BASE_SCHEMA_METADATA,
  COMPONENT_METADATA,
  THEME_SCHEMA_METADATA,
  convertToJsonSchema,
  createComponentSchema,
  exportSchemaToFile,
  fixSchemaReferences
} from "./chunk-ITJC36U6.js";
import {
  generateUnifiedDocumentSchema
} from "./chunk-VLE4CS57.js";
import {
  ERROR_TEMPLATES,
  componentValidator,
  createComponentValidator,
  createThemeValidator,
  getThemeName,
  isColumnsProps,
  isCustomComponentProps,
  isHeadingProps,
  isImageProps,
  isListProps,
  isParagraphProps,
  isReportProps,
  isSectionProps,
  isStandardComponentName,
  isStatisticProps,
  isTableProps,
  isThemeConfig,
  isValidTheme,
  strictComponentValidator,
  strictThemeValidator,
  themeValidator,
  validate,
  validateComponent,
  validateComponentDefinition,
  validateCustomComponentProps,
  validateStrict,
  validateTheme,
  validateThemeJson,
  validateThemeWithEnhancement
} from "./chunk-PMTFSFFW.js";
import {
  GenerateDocumentRequestSchema,
  GenerateDocumentResponseSchema,
  ValidateDocumentRequestSchema,
  ValidateDocumentResponseSchema
} from "./chunk-5VX7H4RI.js";
import {
  docxDocumentBlockTargets
} from "./chunk-N3KCALAB.js";
import {
  JSON_SCHEMA_URLS,
  JsonComponentDefinitionSchema
} from "./chunk-3S3YOEKM.js";
import {
  collectIndentConflicts,
  collectNoteRevisionConflicts,
  collectTextBoxShapeConflicts,
  comprehensiveValidateDocument,
  createDocumentValidator,
  createJsonValidator,
  createValidator,
  deepValidateDocument,
  documentValidator,
  getValidationSummary,
  isValidDocument,
  isValidationSuccess,
  strictDocumentValidator,
  transformValueError,
  transformValueErrors,
  validateAgainstSchema,
  validateBatch,
  validateDocument,
  validateDocumentWithSchema,
  validateJson,
  validateJsonComponent,
  validateJsonDocument,
  validateWithEnhancement
} from "./chunk-MKBB5XJN.js";
import {
  ComponentDefinitionSchema,
  StandardComponentDefinitionSchema
} from "./chunk-WJYTQIJN.js";
import {
  BlockInvocationPropsSchema,
  ChartPropsSchema,
  DividerPropsSchema,
  FLOW_CHILDREN,
  GroupPropsSchema,
  HighchartsPropsSchema,
  ReportPropsSchema,
  STANDARD_COMPONENTS_REGISTRY,
  TextBoxPropsSchema,
  TocPropsSchema,
  getAllStandardComponentNames,
  getStandardComponent
} from "./chunk-JETULU6Z.js";
import {
  BUILT_IN_DOCX_THEME_NAMES,
  ThemeConfigSchema,
  ThemeOverridesSchema,
  createMinimalTheme,
  isValidThemeConfig
} from "./chunk-OVL4RTD7.js";
import {
  DEFAULT_DOCX_RENDERER_ID,
  DOCX_RENDERER_IDS,
  NATIVE_RENDER_MODE,
  NATIVE_VISUAL_ELEMENT_NAMES,
  NativeVisualCanvasSchema,
  NativeVisualElementSchema,
  NativeVisualImagePropsSchema,
  NativeVisualShapePropsSchema,
  NativeVisualTextPropsSchema,
  VisualCanvasBackgroundSchema,
  VisualCanvasSchema,
  VisualNativePropsSchema,
  VisualPropsSchema,
  VisualRasterPropsSchema,
  collectDocxRendererErrors,
  docxComponentDefinitionName,
  docxFlowDefinitionName,
  isNativeVisualProps
} from "./chunk-HDFGXHUB.js";
import {
  ColumnsPropsSchema,
  CommentReplySchema,
  CommentSchema,
  EndnotesSchema,
  FootnotesSchema,
  HeadingPropsSchema,
  ImagePropsSchema,
  ListMarkerFontSchema,
  ListPropsSchema,
  NoteSchema,
  ParagraphPropsSchema,
  RevisionMarkSchema,
  RevisionSchema,
  RevisionSegmentSchema,
  SectionPropsSchema,
  StatisticPropsSchema,
  TablePropsSchema
} from "./chunk-W6KB2SMK.js";
import {
  AlignmentSchema,
  BaseComponentPropsSchema,
  BorderSchema,
  HeadingLevelSchema,
  IndentSchema,
  JustifiedAlignmentSchema,
  LineSpacingSchema,
  MarginsSchema,
  NumberingSchema,
  ParagraphIndentSchema,
  SpacingSchema,
  TabStopLeaderSchema,
  TabStopSchema,
  TabStopTypeSchema,
  TabStopsSchema
} from "./chunk-FYJOHPU2.js";
import "./chunk-XRVPNHNS.js";
import {
  CustomComponentDefinitionSchema,
  TextSpaceAfterComponentSchema,
  TextSpaceAfterPropsSchema
} from "./chunk-7GGTZ4TA.js";

// src/diff/word-diff.ts
var MAX_LCS_CELLS = 1e6;
function tokenizeWords(text) {
  if (!text) return [];
  return text.split(/(\s+)/).filter((t) => t.length > 0);
}
function mergeSegments(segments) {
  const merged = [];
  for (const seg of segments) {
    if (!seg.text) continue;
    const last = merged[merged.length - 1];
    if (last && last.type === seg.type) {
      last.text += seg.text;
    } else {
      merged.push({ ...seg });
    }
  }
  return merged;
}
function normalizeSegments(segments) {
  const folded = [];
  for (let k2 = 0; k2 < segments.length; k2++) {
    const seg = segments[k2];
    if (seg.type === "equal" && /^\s+$/.test(seg.text)) {
      const prev = folded[folded.length - 1];
      const next = segments[k2 + 1];
      if (prev && prev.type !== "equal" && next && next.type !== "equal") {
        folded.push(
          { type: "delete", text: seg.text },
          { type: "insert", text: seg.text }
        );
        continue;
      }
    }
    folded.push({ ...seg });
  }
  const out = [];
  let k = 0;
  while (k < folded.length) {
    if (folded[k].type === "equal") {
      out.push(folded[k]);
      k++;
      continue;
    }
    let deleted = "";
    let inserted = "";
    while (k < folded.length && folded[k].type !== "equal") {
      if (folded[k].type === "delete") deleted += folded[k].text;
      else inserted += folded[k].text;
      k++;
    }
    if (deleted) out.push({ type: "delete", text: deleted });
    if (inserted) out.push({ type: "insert", text: inserted });
  }
  return mergeSegments(out);
}
function diffWords(oldText, newText) {
  if (oldText === newText) {
    return oldText ? [{ type: "equal", text: oldText }] : [];
  }
  const oldTokens = tokenizeWords(oldText);
  const newTokens = tokenizeWords(newText);
  if (oldTokens.length === 0) {
    return mergeSegments([{ type: "insert", text: newText }]);
  }
  if (newTokens.length === 0) {
    return mergeSegments([{ type: "delete", text: oldText }]);
  }
  if (oldTokens.length * newTokens.length > MAX_LCS_CELLS) {
    return mergeSegments([
      { type: "delete", text: oldText },
      { type: "insert", text: newText }
    ]);
  }
  const n = oldTokens.length;
  const m = newTokens.length;
  const lcs = Array.from(
    { length: n + 1 },
    () => new Int32Array(m + 1)
  );
  for (let i2 = n - 1; i2 >= 0; i2--) {
    for (let j2 = m - 1; j2 >= 0; j2--) {
      lcs[i2][j2] = oldTokens[i2] === newTokens[j2] ? lcs[i2 + 1][j2 + 1] + 1 : Math.max(lcs[i2 + 1][j2], lcs[i2][j2 + 1]);
    }
  }
  const segments = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (oldTokens[i] === newTokens[j]) {
      segments.push({ type: "equal", text: oldTokens[i] });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      segments.push({ type: "delete", text: oldTokens[i] });
      i++;
    } else {
      segments.push({ type: "insert", text: newTokens[j] });
      j++;
    }
  }
  while (i < n) {
    segments.push({ type: "delete", text: oldTokens[i] });
    i++;
  }
  while (j < m) {
    segments.push({ type: "insert", text: newTokens[j] });
    j++;
  }
  return normalizeSegments(segments);
}
function stripMarkdown(text) {
  return text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1").replace(
    /(\*\*\*|___)([\s\S]*?)\1|(\*\*|__)([\s\S]*?)\3|(\*|_)([\s\S]*?)\5/g,
    (_match, _d1, bi, _d2, b, _d3, i) => bi ?? b ?? i ?? ""
  );
}

// src/diff/document-diff.ts
var TEXT_COMPONENTS = /* @__PURE__ */ new Set(["paragraph", "heading"]);
function deepEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}
function makeRevision(ctx, segments) {
  return {
    ...ctx.author && { author: ctx.author },
    ...ctx.date && { date: ctx.date },
    segments
  };
}
function rawText(node) {
  const text = node.props?.text;
  return typeof text === "string" ? text.normalize("NFC") : "";
}
function plainText(node) {
  return stripMarkdown(rawText(node));
}
var PLACEHOLDER_PATTERN = /\{[^}]+\}/;
function isEnabled(node) {
  return node.enabled !== false;
}
function notePlaceholdersInChanges(segments, path, component, ctx) {
  if (segments.some((s) => s.type !== "equal" && PLACEHOLDER_PATTERN.test(s.text))) {
    ctx.summary.untracked.push({
      path,
      kind: "modified",
      component,
      detail: "placeholder (e.g. {DATE}) inside inserted/deleted text renders literally in the redline"
    });
  }
}
function propsWithout(props, ...keys) {
  const copy = { ...props || {} };
  for (const key of keys) delete copy[key];
  return copy;
}
var MAX_ALIGN_CELLS = 4e6;
function alignByLcs(oldItems, newItems, key) {
  const oldKeys = oldItems.map(key);
  const newKeys = newItems.map(key);
  let start = 0;
  while (start < oldItems.length && start < newItems.length && oldKeys[start] === newKeys[start]) {
    start++;
  }
  let oldEnd = oldItems.length;
  let newEnd = newItems.length;
  while (oldEnd > start && newEnd > start && oldKeys[oldEnd - 1] === newKeys[newEnd - 1]) {
    oldEnd--;
    newEnd--;
  }
  const prefix = [];
  for (let k = 0; k < start; k++) {
    prefix.push({ op: "equal", oldItem: oldItems[k], newItem: newItems[k] });
  }
  const suffix = [];
  for (let k = 0; k < oldItems.length - oldEnd; k++) {
    suffix.push({
      op: "equal",
      oldItem: oldItems[oldEnd + k],
      newItem: newItems[newEnd + k]
    });
  }
  const n = oldEnd - start;
  const m = newEnd - start;
  const middle = [];
  if (n * m > MAX_ALIGN_CELLS) {
    for (let k = 0; k < n; k++) {
      middle.push({ op: "delete", oldItem: oldItems[start + k] });
    }
    for (let k = 0; k < m; k++) {
      middle.push({ op: "insert", newItem: newItems[start + k] });
    }
    return [...prefix, ...middle, ...suffix];
  }
  const lcs = Array.from(
    { length: n + 1 },
    () => new Int32Array(m + 1)
  );
  for (let i2 = n - 1; i2 >= 0; i2--) {
    for (let j2 = m - 1; j2 >= 0; j2--) {
      lcs[i2][j2] = oldKeys[start + i2] === newKeys[start + j2] ? lcs[i2 + 1][j2 + 1] + 1 : Math.max(lcs[i2 + 1][j2], lcs[i2][j2 + 1]);
    }
  }
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (oldKeys[start + i] === newKeys[start + j]) {
      middle.push({
        op: "equal",
        oldItem: oldItems[start + i],
        newItem: newItems[start + j]
      });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      middle.push({ op: "delete", oldItem: oldItems[start + i] });
      i++;
    } else {
      middle.push({ op: "insert", newItem: newItems[start + j] });
      j++;
    }
  }
  while (i < n) middle.push({ op: "delete", oldItem: oldItems[start + i++] });
  while (j < m) middle.push({ op: "insert", newItem: newItems[start + j++] });
  return [...prefix, ...middle, ...suffix];
}
function pairGap(deleted, inserted, pairable) {
  const usedOld = new Array(deleted.length).fill(false);
  const pairing = new Array(inserted.length).fill(-1);
  let searchFrom = 0;
  for (let j = 0; j < inserted.length; j++) {
    for (let i = searchFrom; i < deleted.length; i++) {
      if (!usedOld[i] && pairable(deleted[i], inserted[j])) {
        usedOld[i] = true;
        pairing[j] = i;
        searchFrom = i + 1;
        break;
      }
    }
  }
  const plan = [];
  const pairs = [];
  let emittedOld = 0;
  for (let j = 0; j < inserted.length; j++) {
    const i = pairing[j];
    if (i >= 0) {
      while (emittedOld < i) {
        if (!usedOld[emittedOld]) {
          plan.push({ kind: "deleted", oldItem: deleted[emittedOld] });
        }
        emittedOld++;
      }
      emittedOld = i + 1;
      plan.push({ kind: "paired", oldItem: deleted[i], newItem: inserted[j] });
      pairs.push({ oldItem: deleted[i], newItem: inserted[j] });
    } else {
      plan.push({ kind: "inserted", newItem: inserted[j] });
    }
  }
  for (let i = emittedOld; i < deleted.length; i++) {
    if (!usedOld[i]) plan.push({ kind: "deleted", oldItem: deleted[i] });
  }
  return { pairs, plan };
}
function diffTextComponent(oldNode, newNode, path, ctx) {
  const oldText = plainText(oldNode);
  const newText = plainText(newNode);
  const propsChanged = !deepEqual(
    propsWithout(oldNode.props, "text", "revision", "comment"),
    propsWithout(newNode.props, "text", "revision", "comment")
  );
  if (propsChanged) {
    ctx.summary.untracked.push({
      path,
      kind: "modified",
      component: newNode.name,
      detail: "formatting/props changed (not expressible as a tracked change); new version rendered"
    });
  }
  if (oldText === newText) {
    if (rawText(oldNode) !== rawText(newNode)) {
      ctx.summary.untracked.push({
        path,
        kind: "modified",
        component: newNode.name,
        detail: "inline formatting or link target changed (markdown-only); new version rendered without a tracked change"
      });
    } else if (!propsChanged) {
      ctx.summary.unchangedBlocks++;
    }
    return newNode;
  }
  ctx.summary.tracked.modified++;
  const segments = diffWords(oldText, newText);
  notePlaceholdersInChanges(segments, path, newNode.name, ctx);
  if (rawText(oldNode) !== oldText || rawText(newNode) !== newText) {
    ctx.summary.untracked.push({
      path,
      kind: "modified",
      component: newNode.name,
      detail: "inline formatting or links flattened to plain text in the redline (revision segments render literally)"
    });
  }
  return {
    ...newNode,
    props: {
      ...revisedTextProps(newNode, path, ctx),
      text: newText,
      revision: makeRevision(ctx, segments)
    }
  };
}
function revisedTextProps(node, path, ctx) {
  const props = { ...node.props ?? {} };
  const dropped = ["footnotes", "endnotes"].filter((kind) => {
    const notes = props[kind];
    return Array.isArray(notes) && notes.length > 0;
  });
  if (dropped.length === 0) return props;
  for (const kind of dropped) delete props[kind];
  ctx.summary.untracked.push({
    path,
    kind: "modified",
    component: node.name,
    detail: `${dropped.join(" and ")} dropped from a tracked-change paragraph (note markers do not resolve inside revision text); the note bodies are not in the redline`
  });
  return props;
}
function insertedTextComponent(node, path, ctx) {
  ctx.summary.tracked.inserted++;
  const text = plainText(node);
  const segments = [{ type: "insert", text }];
  notePlaceholdersInChanges(segments, path, node.name, ctx);
  return {
    ...node,
    props: {
      ...revisedTextProps(node, path, ctx),
      text,
      revision: makeRevision(ctx, segments)
    }
  };
}
function deletedTextComponent(node, path, ctx) {
  ctx.summary.tracked.deleted++;
  const text = plainText(node);
  const segments = [{ type: "delete", text }];
  notePlaceholdersInChanges(segments, path, node.name, ctx);
  return {
    ...node,
    props: {
      ...revisedTextProps(node, path, ctx),
      text: "",
      revision: makeRevision(ctx, segments)
    }
  };
}
function normalizeListItems(node) {
  const items = node.props?.items || [];
  return items.map((item) => {
    const raw = typeof item === "string" ? item : item.text;
    const level = typeof item === "string" ? 0 : item.level || 0;
    return { raw, text: stripMarkdown(raw.normalize("NFC")), level };
  });
}
function diffListComponent(oldNode, newNode, path, ctx) {
  const propsChanged = !deepEqual(
    propsWithout(oldNode.props, "items"),
    propsWithout(newNode.props, "items")
  );
  if (propsChanged) {
    ctx.summary.untracked.push({
      path,
      kind: "modified",
      component: "list",
      detail: "list configuration changed (format/levels/spacing); new version rendered"
    });
  }
  const oldItems = normalizeListItems(oldNode);
  const newItems = normalizeListItems(newNode);
  const stripped = (items) => items.map((i) => ({ text: i.text, level: i.level }));
  if (deepEqual(stripped(oldItems), stripped(newItems))) {
    if (!deepEqual(
      oldItems.map((i) => i.raw),
      newItems.map((i) => i.raw)
    )) {
      ctx.summary.untracked.push({
        path,
        kind: "modified",
        component: "list",
        detail: "inline formatting or link target changed in list items (markdown-only); new version rendered without a tracked change"
      });
    } else if (!propsChanged) {
      ctx.summary.unchangedBlocks++;
    }
    return newNode;
  }
  const ops = alignByLcs(
    oldItems,
    newItems,
    (item) => `${item.level}:${item.text}`
  );
  const outItems = [];
  let changed = false;
  let k = 0;
  while (k < ops.length) {
    const op = ops[k];
    if (op.op === "equal") {
      outItems.push({ text: op.newItem.raw, level: op.newItem.level });
      k++;
      continue;
    }
    const deleted = [];
    const inserted = [];
    while (k < ops.length && ops[k].op !== "equal") {
      const gapOp = ops[k];
      if (gapOp.op === "delete") deleted.push(gapOp.oldItem);
      else if (gapOp.op === "insert") inserted.push(gapOp.newItem);
      k++;
    }
    const { plan } = pairGap(
      deleted,
      inserted,
      (oldItem, newItem) => oldItem.level === newItem.level
    );
    for (const step of plan) {
      changed = true;
      if (step.kind === "paired") {
        const segments = diffWords(step.oldItem.text, step.newItem.text);
        notePlaceholdersInChanges(segments, path, "list", ctx);
        outItems.push({
          text: step.newItem.text,
          level: step.newItem.level,
          revision: makeRevision(ctx, segments)
        });
      } else if (step.kind === "inserted") {
        outItems.push({
          text: step.newItem.text,
          level: step.newItem.level,
          revision: makeRevision(ctx, [
            { type: "insert", text: step.newItem.text }
          ])
        });
      } else {
        outItems.push({
          text: "",
          level: step.oldItem.level,
          revision: makeRevision(ctx, [
            { type: "delete", text: step.oldItem.text }
          ])
        });
      }
    }
  }
  if (changed) ctx.summary.tracked.modified++;
  return {
    ...newNode,
    props: { ...newNode.props, items: outItems }
  };
}
function listWithAllItems(node, type, ctx) {
  if (type === "insert") ctx.summary.tracked.inserted++;
  else ctx.summary.tracked.deleted++;
  const items = normalizeListItems(node).map((item) => ({
    text: type === "insert" ? item.text : "",
    level: item.level,
    revision: makeRevision(ctx, [{ type, text: item.text }])
  }));
  return { ...node, props: { ...node.props, items } };
}
function cellText(cell) {
  if (!cell) return "";
  const content = cell.content;
  if (typeof content === "string")
    return stripMarkdown(content.normalize("NFC"));
  if (content && typeof content === "object") {
    const props = content.props;
    const text = props?.text;
    if (typeof text === "string") return stripMarkdown(text.normalize("NFC"));
  }
  return "";
}
function cellRawText(cell) {
  if (!cell) return "";
  const content = cell.content;
  if (typeof content === "string") return content.normalize("NFC");
  if (content && typeof content === "object") {
    const text = content.props?.text;
    if (typeof text === "string") return text.normalize("NFC");
  }
  return "";
}
function isTextCell(cell) {
  if (!cell) return true;
  const content = cell.content;
  if (content === void 0 || typeof content === "string") return true;
  return typeof content === "object" && content.name === "paragraph";
}
function toRowView(node) {
  const columns = node.props?.columns ?? [];
  const rowCount = columns.reduce(
    (max, column) => Math.max(max, column.cells?.length ?? 0),
    0
  );
  const authoredRows = node.props?.rows ?? [];
  return Array.from({ length: rowCount }, (_, rowIndex) => {
    const cells = columns.map((column) => column.cells?.[rowIndex]);
    return {
      cells,
      key: cells.map(cellText).join("\0"),
      rawKey: cells.map(cellRawText).join("\0"),
      rowProps: authoredRows[rowIndex]
    };
  });
}
function withRows(node, rows, rowProps) {
  const columns = node.props?.columns ?? [];
  const merged = rows.map((row, index) => ({
    ...row.rowProps ?? {},
    ...rowProps[index]
  }));
  return {
    ...node,
    props: {
      ...node.props,
      columns: columns.map((column, colIndex) => ({
        ...column,
        cells: rows.map((row) => row.cells[colIndex] ?? { content: "" })
      })),
      ...merged.some((props) => Object.keys(props).length > 0) && {
        rows: merged
      }
    }
  };
}
function isColumnTable(node) {
  return Array.isArray(node.props?.columns) && !node.props?.headers;
}
function revisedCell(cell, oldCell, oldText, newText, path, ctx) {
  const segments = diffWords(oldText, newText);
  notePlaceholdersInChanges(segments, path, "table", ctx);
  if (cellRawText(oldCell) !== oldText || cellRawText(cell) !== newText) {
    ctx.summary.untracked.push({
      path,
      kind: "modified",
      component: "table",
      detail: "inline formatting or links flattened to plain text in a table cell (revision segments render literally)"
    });
  }
  const base = cell ?? { content: "" };
  return {
    ...base,
    // The revision carries the text, so `content` keeps the new version for
    // readers that ignore tracked changes. A component cell keeps being a
    // component — replacing it with a bare string would silently discard the
    // paragraph's font, alignment and the rest.
    content: revisedCellContent(base.content, newText, path, ctx),
    revision: makeRevision(ctx, segments)
  };
}
function revisedCellContent(content, newText, path, ctx) {
  if (!content || typeof content !== "object") return newText;
  const component = content;
  return {
    ...component,
    props: {
      ...revisedTextProps(component, path, ctx),
      text: newText
    }
  };
}
function diffTableComponent(oldNode, newNode, path, ctx) {
  const oldRows = toRowView(oldNode);
  const newRows = toRowView(newNode);
  const oldColumns = oldNode.props?.columns ?? [];
  const newColumns = newNode.props?.columns ?? [];
  if (oldColumns.length !== newColumns.length) {
    ctx.summary.untracked.push({
      path,
      kind: "modified",
      component: "table",
      detail: "table column count changed (columns are not expressible as a tracked change); new version rendered"
    });
    return newNode;
  }
  const propsChanged = !deepEqual(
    propsWithout(oldNode.props, "columns", "rows"),
    propsWithout(newNode.props, "columns", "rows")
  );
  if (propsChanged) {
    ctx.summary.untracked.push({
      path,
      kind: "modified",
      component: "table",
      detail: "table configuration changed (borders/widths/defaults); new version rendered"
    });
  }
  const headersChanged = !deepEqual(
    oldColumns.map((column) => column.header),
    newColumns.map((column) => column.header)
  );
  if (headersChanged) {
    ctx.summary.untracked.push({
      path,
      kind: "modified",
      component: "table",
      detail: "table header row changed (headers are not row content); new version rendered"
    });
  }
  if (deepEqual(
    oldRows.map((row) => row.key),
    newRows.map((row) => row.key)
  )) {
    if (!deepEqual(
      oldRows.map((row) => row.rawKey),
      newRows.map((row) => row.rawKey)
    )) {
      ctx.summary.untracked.push({
        path,
        kind: "modified",
        component: "table",
        detail: "inline formatting or link target changed in table cells (markdown-only); new version rendered without a tracked change"
      });
    } else if (!propsChanged && !headersChanged && deepEqual(oldRows, newRows)) {
      ctx.summary.unchangedBlocks++;
    }
    return newNode;
  }
  const ops = alignByLcs(oldRows, newRows, (row) => row.key);
  const outRows = [];
  const outProps = [];
  let changed = false;
  const push = (row, props) => {
    outRows.push(row);
    outProps.push(props);
  };
  let k = 0;
  while (k < ops.length) {
    const op = ops[k];
    if (op.op === "equal") {
      push(op.newItem, {});
      k++;
      continue;
    }
    const deleted = [];
    const inserted = [];
    while (k < ops.length && ops[k].op !== "equal") {
      const gapOp = ops[k];
      if (gapOp.op === "delete") deleted.push(gapOp.oldItem);
      else if (gapOp.op === "insert") inserted.push(gapOp.newItem);
      k++;
    }
    const { plan } = pairGap(
      deleted,
      inserted,
      (oldRow, newRow) => oldRow.cells.length === newRow.cells.length && oldRow.cells.every(isTextCell) && newRow.cells.every(isTextCell)
    );
    for (const step of plan) {
      changed = true;
      if (step.kind === "paired") {
        const cells = step.newItem.cells.map((cell, index) => {
          const oldText = cellText(step.oldItem.cells[index]);
          const newText = cellText(cell);
          return oldText === newText ? cell ?? { content: "" } : revisedCell(
            cell,
            step.oldItem.cells[index],
            oldText,
            newText,
            path,
            ctx
          );
        });
        push({ ...step.newItem, cells }, {});
      } else if (step.kind === "inserted") {
        ctx.summary.tracked.inserted++;
        push(step.newItem, { revision: rowRevision(ctx, "insert") });
      } else {
        ctx.summary.tracked.deleted++;
        push(step.oldItem, { revision: rowRevision(ctx, "delete") });
      }
    }
  }
  if (changed) ctx.summary.tracked.modified++;
  return withRows(newNode, outRows, outProps);
}
function tableWithAllRows(node, type, ctx) {
  if (type === "insert") ctx.summary.tracked.inserted++;
  else ctx.summary.tracked.deleted++;
  const rows = toRowView(node);
  return withRows(
    node,
    rows,
    rows.map(() => ({ revision: rowRevision(ctx, type) }))
  );
}
function rowRevision(ctx, type) {
  return {
    type,
    ...ctx.author && { author: ctx.author },
    ...ctx.date && { date: ctx.date }
  };
}
function isContainer(node) {
  return Array.isArray(node.children);
}
function nodesPairable(oldNode, newNode) {
  return oldNode.name === newNode.name;
}
function diffPairedNode(oldNode, newNode, path, ctx) {
  const oldEnabled = isEnabled(oldNode);
  const newEnabled = isEnabled(newNode);
  if (!oldEnabled && !newEnabled) {
    ctx.summary.unchangedBlocks++;
    return newNode;
  }
  if (!oldEnabled && newEnabled) {
    return emitInserted(newNode, path, ctx);
  }
  if (oldEnabled && !newEnabled) {
    const deletedNode = emitDeleted(oldNode, path, ctx);
    return deletedNode ?? newNode;
  }
  if (TEXT_COMPONENTS.has(newNode.name)) {
    return diffTextComponent(oldNode, newNode, path, ctx);
  }
  if (newNode.name === "list") {
    return diffListComponent(oldNode, newNode, path, ctx);
  }
  if (newNode.name === "table" && isColumnTable(oldNode) && isColumnTable(newNode)) {
    return diffTableComponent(oldNode, newNode, path, ctx);
  }
  if (isContainer(newNode) || isContainer(oldNode)) {
    const propsChanged = !deepEqual(oldNode.props, newNode.props);
    if (propsChanged) {
      ctx.summary.untracked.push({
        path,
        kind: "modified",
        component: newNode.name,
        detail: "container props changed; new version rendered"
      });
    }
    return {
      ...newNode,
      children: diffChildren(
        oldNode.children || [],
        newNode.children || [],
        `${path}/children`,
        ctx
      )
    };
  }
  ctx.summary.untracked.push({
    path,
    kind: "modified",
    component: newNode.name,
    detail: `"${newNode.name}" changed (no native tracked-change support); new version rendered`
  });
  return newNode;
}
function emitInserted(node, path, ctx) {
  if (!isEnabled(node)) {
    return node;
  }
  if (TEXT_COMPONENTS.has(node.name)) {
    return insertedTextComponent(node, path, ctx);
  }
  if (node.name === "list") {
    return listWithAllItems(node, "insert", ctx);
  }
  if (node.name === "table" && isColumnTable(node)) {
    return tableWithAllRows(node, "insert", ctx);
  }
  if (isContainer(node)) {
    if (typeof node.props?.title === "string") {
      ctx.summary.untracked.push({
        path,
        kind: "inserted",
        component: node.name,
        detail: `"${node.name}" title rendered without insertion mark (titles are props, not text blocks)`
      });
    }
    return {
      ...node,
      children: diffChildren([], node.children || [], `${path}/children`, ctx)
    };
  }
  ctx.summary.untracked.push({
    path,
    kind: "inserted",
    component: node.name,
    detail: `"${node.name}" added (rendered, but not marked as a tracked insertion)`
  });
  return node;
}
function emitDeleted(node, path, ctx) {
  if (!isEnabled(node)) {
    return null;
  }
  if (TEXT_COMPONENTS.has(node.name)) {
    return deletedTextComponent(node, path, ctx);
  }
  if (node.name === "list") {
    return listWithAllItems(node, "delete", ctx);
  }
  if (node.name === "table" && isColumnTable(node)) {
    return tableWithAllRows(node, "delete", ctx);
  }
  if (isContainer(node)) {
    if (typeof node.props?.title === "string") {
      ctx.summary.untracked.push({
        path,
        kind: "deleted",
        component: node.name,
        detail: `"${node.name}" title still rendered without deletion mark (titles are props, not text blocks)`
      });
    }
    const children = diffChildren(
      node.children || [],
      [],
      `${path}/children`,
      ctx
    );
    return { ...node, children };
  }
  ctx.summary.untracked.push({
    path,
    kind: "deleted",
    component: node.name,
    detail: `"${node.name}" removed (dropped from the redline; Word cannot mark it as a tracked deletion)`
  });
  return null;
}
function diffChildren(oldChildren, newChildren, path, ctx) {
  const ops = alignByLcs(
    oldChildren,
    newChildren,
    (node) => JSON.stringify(node)
  );
  const out = [];
  let k = 0;
  let newIndex = 0;
  while (k < ops.length) {
    const op = ops[k];
    if (op.op === "equal") {
      ctx.summary.unchangedBlocks++;
      out.push(op.newItem);
      k++;
      newIndex++;
      continue;
    }
    const deleted = [];
    const inserted = [];
    while (k < ops.length && ops[k].op !== "equal") {
      const gapOp = ops[k];
      if (gapOp.op === "delete") deleted.push(gapOp.oldItem);
      else if (gapOp.op === "insert") inserted.push(gapOp.newItem);
      k++;
    }
    const { plan } = pairGap(deleted, inserted, nodesPairable);
    for (const step of plan) {
      if (step.kind === "paired") {
        out.push(
          diffPairedNode(step.oldItem, step.newItem, `${path}/${newIndex}`, ctx)
        );
        newIndex++;
      } else if (step.kind === "inserted") {
        out.push(emitInserted(step.newItem, `${path}/${newIndex}`, ctx));
        newIndex++;
      } else {
        const deletedNode = emitDeleted(
          step.oldItem,
          `${path}/${newIndex}`,
          ctx
        );
        if (deletedNode) out.push(deletedNode);
      }
    }
  }
  return out;
}
function diffDocuments(oldDoc, newDoc, options = {}) {
  if (!oldDoc || oldDoc.name !== "docx") {
    throw new Error('Old document: top-level component must be "docx"');
  }
  if (!newDoc || newDoc.name !== "docx") {
    throw new Error('New document: top-level component must be "docx"');
  }
  const summary = {
    tracked: { modified: 0, inserted: 0, deleted: 0 },
    untracked: [],
    unchangedBlocks: 0,
    notes: []
  };
  const ctx = {
    author: options.author,
    date: options.date,
    summary
  };
  const rootPropsChanged = !deepEqual(
    propsWithout(oldDoc.props, "trackRevisions"),
    propsWithout(newDoc.props, "trackRevisions")
  );
  if (rootPropsChanged) {
    summary.untracked.push({
      path: "/props",
      kind: "modified",
      component: "docx",
      detail: "document props changed (theme/metadata/defaults); new version used"
    });
  }
  const children = diffChildren(
    oldDoc.children || [],
    newDoc.children || [],
    "/children",
    ctx
  );
  if (summary.tracked.deleted > 0) {
    summary.notes.push(
      `${summary.tracked.deleted} fully deleted block(s): accepting all changes leaves an empty paragraph behind (OOXML paragraph-mark deletion is not supported by the renderer)`
    );
  }
  const document = {
    ...newDoc,
    props: { ...newDoc.props, trackRevisions: true },
    children
  };
  return { document, summary };
}

// src/index.ts
import {
  fixSchemaReferences as fixSchemaReferences2,
  createComponentSchema as createComponentSchema2,
  createComponentSchemaObject,
  exportSchemaToFile as exportSchemaToFile2
} from "@json-to-office/shared";
import {
  transformValueError as transformValueError2,
  transformValueErrors as transformValueErrors2,
  formatErrorSummary,
  groupErrorsByPath,
  createJsonParseError,
  calculatePosition
} from "@json-to-office/shared";
import {
  DEFAULT_ERROR_CONFIG,
  createErrorConfig,
  ERROR_EMOJIS,
  formatErrorMessage
} from "@json-to-office/shared";
import {
  isUnionSchema,
  isObjectSchema,
  isLiteralSchema,
  getObjectSchemaPropertyNames,
  getLiteralValue,
  extractStandardComponentNames,
  clearComponentNamesCache,
  getSchemaMetadata
} from "@json-to-office/shared";
import {
  isValidSemver,
  parseSemver,
  compareSemver,
  latestVersion
} from "@json-to-office/shared";

// src/validation/parsers/json.ts
import { Value } from "@sinclair/typebox/value";
import { FormatRegistry } from "@sinclair/typebox";
FormatRegistry.Set("uri", (value) => {
  try {
    new URL(value);
    return true;
  } catch {
    if (value.includes(".json") || value.includes("/") || value.includes("\\")) {
      return true;
    }
    return /^https?:\/\/.+/.test(value);
  }
});
FormatRegistry.Set("date-time", (value) => {
  const dateTimeRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
  return dateTimeRegex.test(value) && !isNaN(Date.parse(value));
});
var JsonDocumentParser = class {
  schema = ComponentDefinitionSchema;
  constructor() {
  }
  /**
   * Parse JSON input (string or object) and validate against schema
   * Only supports unified ComponentDefinition structure where document IS a report component
   */
  parse(jsonInput) {
    let parsedObject;
    if (typeof jsonInput === "string") {
      try {
        parsedObject = JSON.parse(jsonInput);
      } catch (error) {
        throw new JsonParsingError(
          "Invalid JSON syntax",
          this.extractJSONSyntaxError(error, jsonInput)
        );
      }
    } else {
      parsedObject = jsonInput;
    }
    if (typeof parsedObject !== "object" || parsedObject === null || !("name" in parsedObject) || parsedObject.name !== "docx") {
      throw new JsonValidationError(
        'Invalid document structure: Document must be a docx component with name="docx"',
        [
          {
            path: "name",
            message: 'Document must be a docx component with name="docx"',
            code: "INVALID_STRUCTURE"
          }
        ]
      );
    }
    if (!Value.Check(this.schema, parsedObject)) {
      const errors = [...Value.Errors(this.schema, parsedObject)];
      throw new JsonValidationError(
        "JSON validation failed",
        this.formatTypeBoxErrors(
          errors,
          typeof jsonInput === "string" ? jsonInput : JSON.stringify(jsonInput, null, 2)
        )
      );
    }
    return parsedObject;
  }
  /**
   * Validate JSON without throwing errors - returns ValidationResult
   */
  validate(jsonInput) {
    try {
      this.parse(jsonInput);
      return {
        valid: true,
        errors: [],
        warnings: []
      };
    } catch (error) {
      if (error instanceof JsonParsingError || error instanceof JsonValidationError) {
        return {
          valid: false,
          errors: error.validationErrors,
          warnings: []
        };
      }
      return {
        valid: false,
        errors: [
          {
            path: "",
            message: error instanceof Error ? error.message : "Unknown validation error",
            code: "UNEXPECTED_ERROR"
          }
        ],
        warnings: []
      };
    }
  }
  /**
   * Parse JSON file content with line number tracking
   */
  parseWithLineNumbers(jsonString) {
    const lines = jsonString.split("\n");
    try {
      return this.parse(jsonString);
    } catch (error) {
      if (error instanceof JsonParsingError || error instanceof JsonValidationError) {
        const enhancedErrors = error.validationErrors.map((err) => ({
          ...err,
          ...this.findLineNumber(err.path, jsonString, lines)
        }));
        if (error instanceof JsonParsingError) {
          throw new JsonParsingError(error.message, enhancedErrors);
        } else {
          throw new JsonValidationError(error.message, enhancedErrors);
        }
      }
      throw error;
    }
  }
  /**
   * Extract JSON syntax error information
   */
  extractJSONSyntaxError(error, jsonString) {
    const message = error.message;
    const positionMatch = message.match(/position\s+(\d+)/i) || message.match(/at\s+(\d+)/i) || message.match(/character\s+(\d+)/i);
    let line = 0;
    let column = 0;
    if (positionMatch) {
      const position = parseInt(positionMatch[1], 10);
      const lines = jsonString.substring(0, position).split("\n");
      line = lines.length;
      column = lines[lines.length - 1].length + 1;
    }
    return [
      {
        path: "",
        message: `JSON syntax error: ${message}`,
        code: "JSON_SYNTAX_ERROR",
        line: line || void 0,
        column: column || void 0,
        suggestions: [
          "Check for missing commas, brackets, or quotes",
          "Validate JSON syntax using a JSON validator",
          "Ensure all strings are properly quoted"
        ]
      }
    ];
  }
  /**
   * Format TypeBox validation errors into ValidationError format
   */
  formatTypeBoxErrors(errors, originalJson) {
    return errors.map((error) => {
      const path = error.path || "";
      const lineInfo = this.findLineNumber(path, originalJson);
      return {
        path,
        message: this.formatTypeBoxErrorMessage(error),
        code: this.getErrorCode(error),
        line: lineInfo.line,
        column: lineInfo.column,
        suggestions: this.generateSuggestions(error)
      };
    });
  }
  /**
   * Format individual TypeBox error message
   */
  formatTypeBoxErrorMessage(error) {
    const path = error.path ? `at "${error.path}"` : "";
    const message = error.message;
    if (message.includes("Expected")) {
      return `${message} ${path}`;
    } else if (message.includes("Required property")) {
      return `${message} ${path}`;
    } else if (message.includes("Unexpected property")) {
      return `${message} ${path}`;
    } else if (message.includes("minimum")) {
      return `Value is too small ${path}. ${message}`;
    } else if (message.includes("maximum")) {
      return `Value is too big ${path}. ${message}`;
    } else {
      return `${message} ${path}`;
    }
  }
  /**
   * Get error code from TypeBox error
   */
  getErrorCode(error) {
    const baseCode = String(error.type || "VALIDATION_ERROR").toUpperCase();
    const path = error.path ? error.path.replace(/\//g, "_").toUpperCase() : "";
    return path ? `${baseCode}_${path}` : baseCode;
  }
  /**
   * Generate helpful suggestions based on error type
   */
  generateSuggestions(error) {
    const suggestions = [];
    const message = error.message;
    if (message.includes("Expected string")) {
      suggestions.push("Ensure the value is wrapped in quotes");
    } else if (message.includes("Expected number")) {
      suggestions.push("Remove quotes around numeric values");
    } else if (message.includes("Expected array")) {
      suggestions.push("Use square brackets [] for arrays");
    } else if (message.includes("Expected object")) {
      suggestions.push("Use curly braces {} for objects");
    } else if (message.includes("Expected literal")) {
      const match = message.match(/Expected literal (.+)/);
      if (match) {
        suggestions.push(`Use the exact value: ${match[1]}`);
      }
    } else if (message.includes("Unexpected property")) {
      suggestions.push("Remove unknown properties or check spelling");
      suggestions.push(
        "Refer to the JSON schema documentation for valid properties"
      );
    } else if (message.includes("Expected union")) {
      suggestions.push(
        "Check that the value matches one of the allowed formats"
      );
      const path = error.path ?? "";
      if (/\/children\/\d+$/.test(path) || path.endsWith("/name")) {
        suggestions.push("Verify the component name is spelled correctly");
      }
    } else if (message.includes("minimum")) {
      const match = message.match(/minimum.*?(\d+)/);
      if (match) {
        if (message.includes("Array")) {
          suggestions.push(`Array must have at least ${match[1]} items`);
        } else {
          suggestions.push(`Value must be at least ${match[1]}`);
        }
      }
    } else if (message.includes("format") && message.includes("uri")) {
      suggestions.push(
        "Ensure the URL is valid and starts with http:// or https://"
      );
    }
    if (suggestions.length === 0) {
      suggestions.push("Check the JSON schema documentation for valid values");
      suggestions.push("Verify the property name and value format");
    }
    return suggestions;
  }
  /**
   * Find line and column number for a given JSON path
   */
  findLineNumber(path, jsonString, lines) {
    if (!path) {
      return {};
    }
    const jsonLines = lines || jsonString.split("\n");
    const pathParts = path.split(".");
    for (let i = 0; i < jsonLines.length; i++) {
      const line = jsonLines[i];
      const lastPathPart = pathParts[pathParts.length - 1];
      if (line.includes(`"${lastPathPart}"`)) {
        const column = line.indexOf(`"${lastPathPart}"`) + 1;
        return {
          line: i + 1,
          column
        };
      }
    }
    return {};
  }
};
var JsonParsingError = class extends Error {
  validationErrors;
  constructor(message, errors) {
    super(message);
    this.name = "JsonParsingError";
    this.validationErrors = errors;
  }
};
var JsonValidationError = class extends Error {
  validationErrors;
  constructor(message, errors) {
    super(message);
    this.name = "JsonValidationError";
    this.validationErrors = errors;
  }
};
function parseJsonComponent(jsonInput) {
  const parser = new JsonDocumentParser();
  return parser.parse(jsonInput);
}
function parseJsonWithLineNumbers(jsonString) {
  const parser = new JsonDocumentParser();
  return parser.parseWithLineNumbers(jsonString);
}

// src/validation/validators/theme.ts
function formatValidationErrors(errors) {
  if (!Array.isArray(errors)) return [];
  return errors.map((error) => {
    if (typeof error === "string") return error;
    const path = error.path ? `${error.path}: ` : "";
    const message = error.message || "Validation error";
    return `${path}${message}`;
  });
}

// src/validation/validators/component.ts
function safeValidateComponentProps(name, props) {
  const componentName = isStandardComponentName(name) ? name : "custom";
  const result = validateComponent(componentName, props);
  if (result.valid) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.errors || [] };
}
function safeValidateComponentDefinition(component) {
  const result = validateComponentDefinition(component);
  if (result.valid) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.errors || [] };
}
function getValidationErrors(props, name) {
  const componentName = name && isStandardComponentName(name) ? name : "custom";
  const result = validateComponent(componentName, props);
  if (result.valid) return [];
  return (result.errors || []).map(
    (e) => e.path ? `${e.path}: ${e.message}` : e.message
  );
}

// src/validation/core/validator.ts
import { Value as Value2 } from "@sinclair/typebox/value";

// src/validation/core/errors.ts
function formatSingleIssue(issue) {
  const path = issue.path || "root";
  let message = issue.message;
  let suggestion;
  let expected;
  let received;
  let options;
  if (message.includes("Expected")) {
    const match = message.match(/Expected (.+) but received (.+)/);
    if (match) {
      expected = match[1];
      received = match[2];
      suggestion = getSuggestionForType(expected, received);
    }
  }
  if (message.includes("Required property")) {
    const match = message.match(/Required property '(.+)'/);
    if (match) {
      message = `Missing required field: ${match[1]}`;
      suggestion = `Add the required field: ${match[1]}`;
    }
  }
  if (message.includes("Unexpected property")) {
    const match = message.match(/Unexpected property '(.+)'/);
    if (match) {
      message = `Unknown property: ${match[1]}`;
      suggestion = `Remove the unknown property: ${match[1]}`;
    }
  }
  if (message.includes("Expected union")) {
    message = "Value does not match any of the expected types";
    suggestion = "Check the documentation for valid format options";
  }
  if (message.includes("Expected literal")) {
    const match = message.match(/Expected literal (.+)/);
    if (match) {
      expected = match[1];
      message = `Expected value: ${expected}`;
      suggestion = `Use the exact value: ${expected}`;
    }
  }
  if (message.includes("String length")) {
    const minMatch = message.match(/minimum length of (\d+)/);
    const maxMatch = message.match(/maximum length of (\d+)/);
    if (minMatch) {
      message = `String must be at least ${minMatch[1]} characters long`;
      suggestion = `Add more characters to meet the minimum length of ${minMatch[1]}`;
    } else if (maxMatch) {
      message = `String must be at most ${maxMatch[1]} characters long`;
      suggestion = `Reduce to ${maxMatch[1]} characters or less`;
    }
  }
  if (message.includes("Expected number")) {
    const minMatch = message.match(/minimum value of ([\d.]+)/);
    const maxMatch = message.match(/maximum value of ([\d.]+)/);
    if (minMatch) {
      message = `Number must be greater than or equal to ${minMatch[1]}`;
      suggestion = `Use a value of ${minMatch[1]} or higher`;
    } else if (maxMatch) {
      message = `Number must be less than or equal to ${maxMatch[1]}`;
      suggestion = `Use a value of ${maxMatch[1]} or lower`;
    }
  }
  if (message.includes("Array length")) {
    const minMatch = message.match(/minimum length of (\d+)/);
    const maxMatch = message.match(/maximum length of (\d+)/);
    if (minMatch) {
      message = `Array must have at least ${minMatch[1]} items`;
      suggestion = `Add more items to meet the minimum of ${minMatch[1]}`;
    } else if (maxMatch) {
      message = `Array must have at most ${maxMatch[1]} items`;
      suggestion = `Remove items to meet the maximum of ${maxMatch[1]}`;
    }
  }
  if (message.includes("format")) {
    if (message.includes("email")) {
      message = "Invalid email address format";
      suggestion = "Use a valid email format like user@example.com";
    } else if (message.includes("uri") || message.includes("url")) {
      message = "Invalid URL format";
      suggestion = "Use a valid URL format like https://example.com";
    } else if (message.includes("date-time")) {
      message = "Invalid date-time format";
      suggestion = "Use ISO 8601 format like 2024-01-01T00:00:00Z";
    }
  }
  if (message.includes("pattern")) {
    message = "String does not match the required pattern";
    suggestion = "Check the format requirements for this field";
  }
  return {
    path,
    message,
    code: String(issue.type || "validation_error"),
    suggestion,
    expected,
    received,
    options
  };
}
function getSuggestionForType(expected, received) {
  if (expected === "number" && received === "string") {
    return "Remove quotes or convert the string to a number";
  }
  if (expected === "string" && received === "number") {
    return "Add quotes or convert the number to a string";
  }
  if (expected === "boolean") {
    return "Use true or false (without quotes)";
  }
  if (expected === "array" && received === "object") {
    return "Use square brackets [] for arrays instead of curly braces {}";
  }
  if (expected === "object" && received === "array") {
    return "Use curly braces {} for objects instead of square brackets []";
  }
  if (received === "null" || received === "undefined") {
    return `Provide a valid ${expected} value or mark the field as optional`;
  }
  return void 0;
}
function formatTypeBoxError(errors) {
  return errors.map(formatSingleIssue);
}
function formatTypeBoxErrorStrings(errors) {
  return formatTypeBoxError(errors).map((err) => {
    let msg = `${err.path}: ${err.message}`;
    if (err.suggestion) {
      msg += ` (Suggestion: ${err.suggestion})`;
    }
    return msg;
  });
}
function getErrorSummary(errors) {
  const summary = /* @__PURE__ */ new Map();
  for (const formattedError of formatTypeBoxError(errors)) {
    const existing = summary.get(formattedError.path) || [];
    existing.push(formattedError);
    summary.set(formattedError.path, existing);
  }
  return summary;
}
function formatErrorReport(errors) {
  const formattedErrors = formatTypeBoxError(errors);
  const summary = getErrorSummary(errors);
  let report = `Validation failed with ${formattedErrors.length} error${formattedErrors.length > 1 ? "s" : ""}:

`;
  for (const [path, pathErrors] of summary) {
    report += `\u{1F4CD} ${path}:
`;
    for (const err of pathErrors) {
      report += `   \u274C ${err.message}
`;
      if (err.suggestion) {
        report += `   \u{1F4A1} ${err.suggestion}
`;
      }
      if (err.expected && err.received) {
        report += `   \u{1F4CB} Expected: ${err.expected}, Received: ${err.received}
`;
      }
      if (err.options) {
        report += `   \u{1F4CB} Valid options: ${err.options.join(", ")}
`;
      }
    }
    report += "\n";
  }
  return report;
}
function hasCriticalErrors(errors) {
  return errors.some((issue) => {
    if (issue.message.includes("Required property")) {
      return true;
    }
    if (issue.path.includes("name") && issue.message.includes("Expected literal")) {
      return true;
    }
    if (issue.message.includes("Expected union") || issue.message.includes("Never")) {
      return true;
    }
    return false;
  });
}
function getValidationContext(path) {
  if (!path || path === "root") return "document root";
  const pathParts = path.split(".");
  if (pathParts.includes("children")) {
    const childIndex = pathParts.indexOf("children");
    if (pathParts.length > childIndex + 1) {
      const index = pathParts[childIndex + 1];
      return `component at index ${index}`;
    }
  }
  if (pathParts.includes("props")) {
    return "props section";
  }
  if (pathParts.includes("theme")) {
    return "theme configuration";
  }
  return pathParts.join(" > ");
}
var formatValidationError = formatTypeBoxError;
var formatValidationErrorStrings = formatTypeBoxErrorStrings;

// src/validation/core/validator.ts
var COMPONENT_SCHEMA_MAP = {
  report: ReportPropsSchema,
  section: SectionPropsSchema,
  heading: HeadingPropsSchema,
  paragraph: ParagraphPropsSchema,
  columns: ColumnsPropsSchema,
  image: ImagePropsSchema,
  statistic: StatisticPropsSchema,
  table: TablePropsSchema,
  list: ListPropsSchema
};
function validateComponent2(name, props, options) {
  const schema = COMPONENT_SCHEMA_MAP[name];
  if (!schema) {
    if (Value2.Check(CustomComponentDefinitionSchema, props)) {
      return {
        success: true,
        data: props
      };
    }
    const errors2 = [...Value2.Errors(CustomComponentDefinitionSchema, props)];
    const formattedErrors2 = formatTypeBoxError(errors2);
    return {
      success: false,
      errors: formattedErrors2,
      errorStrings: formatTypeBoxErrorStrings(errors2),
      report: options?.includeReport ? formatErrorReport(errors2) : void 0,
      hasCriticalErrors: options?.checkCritical ? hasCriticalErrors(errors2) : void 0
    };
  }
  if (Value2.Check(schema, props)) {
    return {
      success: true,
      data: props
    };
  }
  const errors = [...Value2.Errors(schema, props)];
  const formattedErrors = formatTypeBoxError(errors);
  return {
    success: false,
    errors: formattedErrors,
    errorStrings: formatTypeBoxErrorStrings(errors),
    report: options?.includeReport ? formatErrorReport(errors) : void 0,
    hasCriticalErrors: options?.checkCritical ? hasCriticalErrors(errors) : void 0
  };
}
function validateComponentDefinition2(component, options) {
  const maxDepth = options?.maxDepth ?? 10;
  const currentDepth = options?.currentDepth ?? 0;
  if (currentDepth > maxDepth) {
    return {
      success: false,
      errors: [
        {
          path: "children",
          message: `Maximum nesting depth (${maxDepth}) exceeded`,
          code: "custom",
          suggestion: "Reduce the nesting level of components"
        }
      ],
      errorStrings: [`Maximum nesting depth (${maxDepth}) exceeded`],
      hasCriticalErrors: true
    };
  }
  if (Value2.Check(ComponentDefinitionSchema, component)) {
    const data = component;
    const warnings = [];
    if (data.children && Array.isArray(data.children)) {
      for (let i = 0; i < data.children.length; i++) {
        const nestedResult = validateComponentDefinition2(data.children[i], {
          ...options,
          currentDepth: currentDepth + 1
        });
        if (!nestedResult.success) {
          const nestedErrors = nestedResult.errors?.map((err) => ({
            ...err,
            path: `children[${i}].${err.path}`
          }));
          return {
            success: false,
            errors: nestedErrors,
            errorStrings: nestedErrors?.map(
              (err) => `${err.path}: ${err.message}`
            ),
            report: options?.includeReport ? `Validation failed in nested component at index ${i}:
${nestedResult.report}` : void 0,
            hasCriticalErrors: nestedResult.hasCriticalErrors
          };
        }
        if (nestedResult.warnings) {
          warnings.push(
            ...nestedResult.warnings.map((w) => `children[${i}]: ${w}`)
          );
        }
      }
    }
    return {
      success: true,
      data: component,
      warnings: warnings.length > 0 ? warnings : void 0
    };
  }
  const errors = [...Value2.Errors(ComponentDefinitionSchema, component)];
  const formattedErrors = formatTypeBoxError(errors);
  return {
    success: false,
    errors: formattedErrors,
    errorStrings: formatTypeBoxErrorStrings(errors),
    report: options?.includeReport ? formatErrorReport(errors) : void 0,
    hasCriticalErrors: options?.checkCritical ? hasCriticalErrors(errors) : void 0
  };
}
function validateComponents2(components, options) {
  const results = [];
  let criticalCount = 0;
  for (const { name, props } of components) {
    const result = name in COMPONENT_SCHEMA_MAP ? validateComponent2(name, props, options) : validateComponentDefinition2(props, options);
    results.push(result);
    if (result.hasCriticalErrors) {
      criticalCount++;
    }
    if (!result.success && options?.stopOnFirst) {
      break;
    }
  }
  const valid = results.filter((r) => r.success).length;
  const invalid = results.length - valid;
  return {
    success: invalid === 0,
    results,
    summary: {
      total: results.length,
      valid,
      invalid,
      criticalErrors: criticalCount
    }
  };
}
function transformAndValidate(name, data, transformer) {
  try {
    const transformed = transformer ? transformer(data) : data;
    return validateComponent2(name, transformed, {
      includeReport: true,
      checkCritical: true
    });
  } catch (error) {
    return {
      success: false,
      errors: [
        {
          path: "root",
          message: `Transformation failed: ${error instanceof Error ? error.message : String(error)}`,
          code: "custom"
        }
      ],
      errorStrings: [
        `Transformation failed: ${error instanceof Error ? error.message : String(error)}`
      ],
      hasCriticalErrors: true
    };
  }
}
function createValidatedComponent(name, partialConfig) {
  const schema = COMPONENT_SCHEMA_MAP[name];
  if (!schema) {
    return {
      success: false,
      errors: [
        {
          path: "name",
          message: `Unknown component name: ${name}`,
          code: "custom",
          suggestion: `Use one of: ${Object.keys(COMPONENT_SCHEMA_MAP).join(", ")}`
        }
      ],
      errorStrings: [`Unknown component name: ${name}`],
      hasCriticalErrors: true
    };
  }
  if (Value2.Check(schema, partialConfig)) {
    return {
      success: true,
      data: partialConfig
    };
  }
  const errors = [...Value2.Errors(schema, partialConfig)];
  const formattedErrors = formatTypeBoxError(errors);
  return {
    success: false,
    errors: formattedErrors,
    errorStrings: formatTypeBoxErrorStrings(errors),
    report: formatErrorReport(errors),
    hasCriticalErrors: hasCriticalErrors(errors)
  };
}
function isValidComponent(name, props) {
  const result = validateComponent2(name, props);
  return result.success;
}

// src/types/components.ts
var STANDARD_COMPONENTS = [
  "chart",
  "columns",
  "divider",
  "heading",
  "highcharts",
  "image",
  "list",
  "paragraph",
  "docx",
  "section",
  "statistic",
  "table",
  "text-box",
  "toc",
  "visual",
  "block",
  "group"
];
var STANDARD_COMPONENTS_SET = new Set(STANDARD_COMPONENTS);
function isReportComponent(component) {
  return component.name === "docx";
}
function isSectionComponent(component) {
  return component.name === "section";
}
function isColumnsComponent(component) {
  return component.name === "columns";
}
function isHeadingComponent(component) {
  return component.name === "heading";
}
function isParagraphComponent(component) {
  return component.name === "paragraph";
}
function isImageComponent(component) {
  return component.name === "image";
}
function isTextBoxComponent(component) {
  return component.name === "text-box";
}
function isStatisticComponent(component) {
  return component.name === "statistic";
}
function isTableComponent(component) {
  return component.name === "table";
}
function isListComponent(component) {
  return component.name === "list";
}
function isTocComponent(component) {
  return component.name === "toc";
}
function isHighchartsComponent(component) {
  return component.name === "highcharts";
}
function isVisualComponent(component) {
  return component.name === "visual";
}
function isTextSpaceAfterComponent(component) {
  return component.name === "text-space-after";
}

// src/index.ts
var SHARED_DOCX_VERSION = "1.0.0";
export {
  AlignmentSchema,
  BASE_SCHEMA_METADATA,
  BUILT_IN_DOCX_THEME_NAMES,
  BaseComponentPropsSchema,
  BlockInvocationPropsSchema,
  BorderSchema,
  COMPONENT_METADATA,
  ChartPropsSchema,
  ColumnsPropsSchema,
  CommentReplySchema,
  CommentSchema,
  ComponentDefinitionSchema,
  CustomComponentDefinitionSchema,
  DEFAULT_DOCX_RENDERER_ID,
  DEFAULT_ERROR_CONFIG,
  DOCX_RENDERER_IDS,
  DividerPropsSchema,
  ERROR_EMOJIS,
  ERROR_TEMPLATES,
  EndnotesSchema,
  FLOW_CHILDREN,
  FootnotesSchema,
  GenerateDocumentRequestSchema,
  GenerateDocumentResponseSchema,
  GroupPropsSchema,
  HeadingLevelSchema,
  HeadingPropsSchema,
  HighchartsPropsSchema,
  ImagePropsSchema,
  IndentSchema,
  JSON_SCHEMA_URLS,
  JsonComponentDefinitionSchema,
  JsonDocumentParser,
  JsonParsingError,
  JsonValidationError,
  JustifiedAlignmentSchema,
  LineSpacingSchema,
  ListMarkerFontSchema,
  ListPropsSchema,
  MarginsSchema,
  NATIVE_RENDER_MODE,
  NATIVE_VISUAL_ELEMENT_NAMES,
  NativeVisualCanvasSchema,
  NativeVisualElementSchema,
  NativeVisualImagePropsSchema,
  NativeVisualShapePropsSchema,
  NativeVisualTextPropsSchema,
  NoteSchema,
  NumberingSchema,
  ParagraphIndentSchema,
  ParagraphPropsSchema,
  ReportPropsSchema,
  RevisionMarkSchema,
  RevisionSchema,
  RevisionSegmentSchema,
  SHARED_DOCX_VERSION,
  STANDARD_COMPONENTS,
  STANDARD_COMPONENTS_REGISTRY,
  STANDARD_COMPONENTS_SET,
  SectionPropsSchema,
  SpacingSchema,
  StandardComponentDefinitionSchema,
  StatisticPropsSchema,
  THEME_SCHEMA_METADATA,
  TabStopLeaderSchema,
  TabStopSchema,
  TabStopTypeSchema,
  TabStopsSchema,
  TablePropsSchema,
  TextBoxPropsSchema,
  TextSpaceAfterComponentSchema,
  TextSpaceAfterPropsSchema,
  ThemeConfigSchema,
  ThemeOverridesSchema,
  TocPropsSchema,
  ValidateDocumentRequestSchema,
  ValidateDocumentResponseSchema,
  VisualCanvasBackgroundSchema,
  VisualCanvasSchema,
  VisualNativePropsSchema,
  VisualPropsSchema,
  VisualRasterPropsSchema,
  calculatePosition,
  clearComponentNamesCache,
  collectDocxRendererErrors,
  collectIndentConflicts,
  collectNoteRevisionConflicts,
  collectTextBoxShapeConflicts,
  compareSemver,
  componentValidator,
  comprehensiveValidateDocument,
  convertToJsonSchema as convertDocxToJsonSchema,
  convertToJsonSchema,
  createComponentSchema2 as createComponentSchema,
  createComponentValidator,
  createDocumentValidator,
  createComponentSchema as createDocxComponentSchema,
  createErrorConfig,
  createJsonParseError,
  createJsonValidator,
  createMinimalTheme,
  createThemeValidator,
  createValidatedComponent,
  createValidator,
  deepValidateDocument,
  diffDocuments,
  diffWords,
  documentValidator,
  docxComponentDefinitionName,
  docxDocumentBlockTargets,
  docxFlowDefinitionName,
  exportSchemaToFile as exportDocxSchemaToFile,
  exportSchemaToFile2 as exportSchemaToFile,
  extractStandardComponentNames,
  fixSchemaReferences as fixDocxSchemaReferences,
  fixSchemaReferences2 as fixSchemaReferences,
  formatErrorMessage,
  formatErrorReport,
  formatErrorSummary,
  formatValidationError,
  formatValidationErrorStrings,
  formatValidationErrors,
  generateUnifiedDocumentSchema,
  getAllStandardComponentNames,
  getErrorSummary,
  getLiteralValue,
  getObjectSchemaPropertyNames,
  getSchemaMetadata,
  getStandardComponent,
  getThemeName,
  getValidationContext,
  getValidationErrors,
  getValidationSummary,
  groupErrorsByPath,
  hasCriticalErrors,
  isColumnsComponent,
  isColumnsProps,
  isCustomComponentProps,
  isHeadingComponent,
  isHeadingProps,
  isHighchartsComponent,
  isImageComponent,
  isImageProps,
  isListComponent,
  isListProps,
  isLiteralSchema,
  isNativeVisualProps,
  isObjectSchema,
  isParagraphComponent,
  isParagraphProps,
  isReportComponent,
  isReportProps,
  isSectionComponent,
  isSectionProps,
  isStandardComponentName,
  isStatisticComponent,
  isStatisticProps,
  isTableComponent,
  isTableProps,
  isTextBoxComponent,
  isTextSpaceAfterComponent,
  isThemeConfig,
  isTocComponent,
  isUnionSchema,
  isValidComponent,
  isValidDocument,
  isValidSemver,
  isValidTheme,
  isValidThemeConfig,
  isValidTheme as isValidThemeJson,
  isValidationSuccess,
  isVisualComponent,
  latestVersion,
  parseJsonComponent,
  parseJsonWithLineNumbers,
  parseSemver,
  safeValidateComponentDefinition,
  safeValidateComponentProps,
  createComponentSchemaObject as sharedCreateComponentSchemaObject,
  strictComponentValidator,
  strictDocumentValidator,
  strictThemeValidator,
  stripMarkdown,
  themeValidator,
  transformAndValidate,
  transformValueError as transformDocxValueError,
  transformValueErrors as transformDocxValueErrors,
  transformValueError2 as transformValueError,
  transformValueErrors2 as transformValueErrors,
  validate,
  validateAgainstSchema,
  validateBatch,
  validateComponent2 as validateComponent,
  validateComponentDefinition2 as validateComponentDefinition,
  validateComponent as validateComponentProps,
  validateComponents2 as validateComponents,
  validateCustomComponentProps,
  validateDocument,
  validateDocumentWithSchema,
  validateJson,
  validateJsonDocument as validateJsonComponent,
  validateJsonComponent as validateJsonComponentDoc,
  validateJsonDocument,
  validateStrict,
  validateTheme,
  validateThemeJson,
  validateThemeWithEnhancement,
  validateWithEnhancement
};
//# sourceMappingURL=index.js.map